import React from 'react';
import { Badge } from '../ui/badge';
import { Gauge } from 'lucide-react';
import { FLAG_LABELS } from './ResponseQuestionRow';

const ScoreTrend = ({ history }) => {
  const W = 260, H = 56, PAD = 6;
  const pts = history.map(h => h.overall);
  const x = (i) => PAD + (i * (W - PAD * 2)) / Math.max(pts.length - 1, 1);
  const y = (v) => H - PAD - ((v || 0) / 100) * (H - PAD * 2);
  const line = (key) => pts.map((p, i) => `${x(i)},${y(p[key])}`).join(' ');
  return (
    <div className="flex items-center gap-3 py-1" data-testid="rfp-resp-score-trend">
      <svg width={W} height={H} className="shrink-0">
        <line x1={PAD} y1={y(50)} x2={W - PAD} y2={y(50)} stroke="#E2E8F0" strokeDasharray="3 3" />
        <polyline points={line('confidence_score')} fill="none" stroke="#4ABFCF" strokeWidth="2" strokeLinejoin="round" />
        <polyline points={line('capability_score')} fill="none" stroke="#0F2040" strokeWidth="2" strokeLinejoin="round" strokeOpacity="0.55" />
        {pts.map((p, i) => <circle key={i} cx={x(i)} cy={y(p.confidence_score)} r="2.5" fill="#4ABFCF" />)}
      </svg>
      <div className="text-[9px] text-slate-500 space-y-0.5">
        <p><span className="inline-block w-2.5 h-0.5 bg-[#4ABFCF] align-middle mr-1" />Response confidence</p>
        <p><span className="inline-block w-2.5 h-0.5 bg-[#0F2040]/60 align-middle mr-1" />Capability match</p>
      </div>
    </div>
  );
};

export const ResponseScoreCard = ({ score, history }) => (
  <div className="border border-[#4ABFCF]/40 bg-[#4ABFCF]/5 rounded-md p-4 space-y-3" data-testid="rfp-resp-score-card">
    <div className="flex items-center gap-6 flex-wrap">
      <span className="text-xs font-semibold text-[#0F2040] flex items-center gap-1.5">
        <Gauge className="w-4 h-4 text-[#4ABFCF]" />Response Score
      </span>
      <div>
        <span className="text-2xl font-bold text-[#0F2040]" data-testid="rfp-resp-confidence-score">{score.overall.confidence_score}%</span>
        <span className="text-[10px] text-slate-500 ml-1.5">response confidence</span>
      </div>
      <div>
        <span className="text-2xl font-bold text-[#0F2040]" data-testid="rfp-resp-capability-score">{score.overall.capability_score}%</span>
        {score.overall.weighted_score != null && <span className="text-[10px] text-[#4ABFCF] font-semibold ml-1.5" data-testid="rfp-resp-weighted-score" title="Average client-weighted fit across sheets that carry category weights">· client-weighted {score.overall.weighted_score}%</span>}
        <span className="text-[10px] text-slate-500 ml-1.5">capability match</span>
      </div>
      <span className="text-[9px] text-slate-400">{score.scored_at ? new Date(score.scored_at).toLocaleString() : ''}</span>
    </div>
    {score.narrative && (
      <p className="text-xs text-slate-600 leading-relaxed" data-testid="rfp-resp-narrative">{score.narrative}</p>
    )}
    {(score.by_document || []).map(d => (
      <div key={d.doc_id || d.document} className="flex items-center gap-2 flex-wrap text-[10px] text-slate-500">
        <span className="font-medium text-slate-600 truncate max-w-[220px]">{d.document}</span>
        <span>confidence {d.confidence_score}% · capability {d.capability_score}%{d.weighted_score != null ? ` · client-weighted ${d.weighted_score}%` : ''}</span>
        {Object.entries(d.flags || {}).filter(([, n]) => n > 0).map(([f, n]) => (
          <Badge key={f} variant="outline" className="text-[8px]">{n} {FLAG_LABELS[f] || f}</Badge>
        ))}
      </div>
    ))}
    {(history || []).length > 1 && (
      <div className="pt-2 border-t border-[#4ABFCF]/20 space-y-0.5" data-testid="rfp-resp-score-history">
        <p className="text-[9px] font-semibold text-slate-400 uppercase tracking-wide">Rescore history</p>
        <ScoreTrend history={history} />
        {[...history].reverse().slice(0, 6).map((h, i, arr) => {
          const prev = arr[i + 1];
          const delta = prev ? Math.round((h.overall.confidence_score - prev.overall.confidence_score) * 10) / 10 : null;
          return (
            <p key={h.scored_at} className="text-[10px] text-slate-500">
              {h.scored_at ? new Date(h.scored_at).toLocaleString() : ''} — confidence {h.overall.confidence_score}% · capability {h.overall.capability_score}%
              {delta !== null && delta !== 0 && (
                <span className={delta > 0 ? 'text-emerald-600 font-semibold' : 'text-rose-600 font-semibold'}> ({delta > 0 ? '+' : ''}{delta})</span>
              )}
              {h.scored_by ? ` · ${h.scored_by}` : ''}
            </p>
          );
        })}
      </div>
    )}
  </div>
);
